// ── SmartTraQ Tasks Page (v3 — Right Panel, Subtasks, Reminders) ──
import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { T, isDark, Ic, sIG, sSG, sBG, sB2G, BF } from './theme.jsx';
import { HF, TASK_STATUS, PRI } from './constants';
import { fd, relTime } from './utils';
import { Fld, EmptyPg, DotsMenu, Bdg } from './ui.jsx';
import { useI18n } from './i18n.js';

/* ── helpers ── */
const priC={Urgent:"#EF4444",High:"#F59E0B",Medium:"#3B82F6",Low:"#10B981"};
const typeIc={order:"pkg",shipment:"ship",customer:"usr",vendor:"bld",general:"tasks"};
const typeC={order:"#3B82F6",shipment:"#06B6D4",customer:"#10B981",vendor:"#F59E0B",general:"#64748B"};
const stMap=Object.fromEntries(TASK_STATUS.map(s=>[s.id,s]));
const stColor=id=>(stMap[id]||stMap.todo).c;
const stName=id=>(stMap[id]||stMap.todo).n;
const subProg=t=>{const s=(t.subtasks||[]);if(!s.length)return null;const d=s.filter(x=>x.done).length;return{done:d,total:s.length,pct:Math.round(d/s.length*100)}};

function TasksPg({tasks,setTasks,orders,ships,custs,vends,users,curUser,onAddTask,goOrder,goCust,goVend,onSyncTask,onDelTask}) {
  const {t}=useI18n();
  const [filter,setFilter]=useState("active");
  const [search,setSearch]=useState("");
  const [viewMode,setViewMode]=useState("board");
  const [showNew,setShowNew]=useState(false);
  const [vwId,setVwId]=useState(null);
  const [groupBy,setGroupBy]=useState("status");
  const [myTasks,setMyTasks]=useState(false);
  const [newTask,setNewTask]=useState({title:"",due:"",priority:"Medium",type:"general",note:"",assignees:[],status:"todo"});

  const vwTask=useMemo(()=>(tasks||[]).find(t=>t.id===vwId)||null,[tasks,vwId]);

  /* filter pipeline */
  const allTasks=useMemo(()=>{
    let list=(tasks||[]).filter(t=>{
      if(search){const s=search.toLowerCase();if(!t.title?.toLowerCase().includes(s)&&!t.type?.toLowerCase().includes(s)&&!(t.note||"").toLowerCase().includes(s))return false}
      if(myTasks&&curUser?.name){if(!(t.assignees||[]).includes(curUser.name))return false}
      if(filter==="active")return(t.status||"todo")!=="done";
      if(filter==="overdue")return(t.status||"todo")!=="done"&&t.due&&new Date(t.due+"T23:59:59")<new Date();
      if(filter==="today"){const td=new Date();const dd=new Date((t.due||"")+"T00:00:00");return(t.status||"todo")!=="done"&&dd.toDateString()===td.toDateString()}
      if(filter==="done")return(t.status||"todo")==="done";
      return true;
    });
    const pa={Urgent:0,High:1,Medium:2,Low:3};
    list.sort((a,b)=>{if((pa[a.priority]||2)!==(pa[b.priority]||2))return(pa[a.priority]||2)-(pa[b.priority]||2);return new Date(a.due||0)-new Date(b.due||0)});
    return list;
  },[tasks,filter,search,myTasks,curUser]);

  /* board items — search+myTasks only, no status filter (so Done column always visible) */
  const boardItems=useMemo(()=>{
    let list=(tasks||[]).filter(t=>{
      if(search){const s=search.toLowerCase();if(!t.title?.toLowerCase().includes(s)&&!t.type?.toLowerCase().includes(s)&&!(t.note||"").toLowerCase().includes(s))return false}
      if(myTasks&&curUser?.name){if(!(t.assignees||[]).includes(curUser.name))return false}
      return true;
    });
    const pa={Urgent:0,High:1,Medium:2,Low:3};
    list.sort((a,b)=>{if((pa[a.priority]||2)!==(pa[b.priority]||2))return(pa[a.priority]||2)-(pa[b.priority]||2);return new Date(a.due||0)-new Date(b.due||0)});
    return list;
  },[tasks,search,myTasks,curUser]);

  /* counts */
  const counts=useMemo(()=>{
    const all=(tasks||[]);
    return{
      active:all.filter(t=>(t.status||"todo")!=="done").length,
      overdue:all.filter(t=>(t.status||"todo")!=="done"&&t.due&&new Date(t.due+"T23:59:59")<new Date()).length,
      today:all.filter(t=>{if((t.status||"todo")==="done"||!t.due)return false;const td=new Date();const dd=new Date(t.due+"T00:00:00");return dd.toDateString()===td.toDateString()}).length,
      done:all.filter(t=>(t.status||"todo")==="done").length
    };
  },[tasks]);

  /* ── mutations ── */
  const syncUp=useCallback((upd)=>{
    setTasks(prev=>prev.map(x=>x.id===upd.id?upd:x));
    onSyncTask&&onSyncTask(upd,"update");
  },[setTasks,onSyncTask]);

  const changeStatus=useCallback((t,newStatus)=>{
    const isDone=newStatus==="done";
    syncUp({...t,status:newStatus,done:isDone,doneAt:isDone?new Date().toISOString():null});
  },[syncUp]);

  const toggleDone=useCallback(t=>{
    const newSt=(t.status||"todo")==="done"?"todo":"done";
    changeStatus(t,newSt);
  },[changeStatus]);

  const deleteTask=useCallback(t=>{
    if(!confirm("Delete task '"+t.title+"'?"))return;
    setTasks(prev=>prev.filter(x=>x.id!==t.id));
    onDelTask&&onDelTask(t);
    if(vwId===t.id)setVwId(null);
  },[setTasks,onDelTask,vwId]);

  const dueLabel=t=>{
    if(!t.due)return null;
    const d=Math.ceil((new Date(t.due+"T23:59:59")-new Date())/864e5);
    if((t.status||"todo")==="done")return{text:fd(t.due),color:T.tm};
    if(d<0)return{text:Math.abs(d)+"d overdue",color:"#EF4444"};
    if(d===0)return{text:"Due today",color:"#F59E0B"};
    if(d===1)return{text:"Tomorrow",color:"#F59E0B"};
    if(d<=7)return{text:d+"d left",color:"#3B82F6"};
    return{text:fd(t.due),color:T.tm};
  };

  const goRef=t=>{if(!t.refId)return;if(t.type==="order"&&goOrder)goOrder(t.refId);else if(t.type==="customer"&&goCust)goCust(t.refId);else if(t.type==="vendor"&&goVend)goVend(t.refId)};

  /* ── drag handlers ── */
  const onDragStart=(e,t)=>{e.dataTransfer.setData("tid",t.id);e.currentTarget.style.opacity=".5"};
  const onDragEnd=e=>{e.currentTarget.style.opacity="1"};
  const onColDragOver=(e,col)=>{e.preventDefault();e.currentTarget.style.background=col.c+"08";e.currentTarget.style.borderColor=col.c+"40"};
  const onColDragLeave=e=>{e.currentTarget.style.background=T.sf+"90";e.currentTarget.style.borderColor=isDark()?"rgba(255,255,255,.05)":T.bd+"50"};
  const onColDrop=(e,colId)=>{
    e.preventDefault();
    e.currentTarget.style.background=T.sf+"90";
    e.currentTarget.style.borderColor=isDark()?"rgba(255,255,255,.05)":T.bd+"50";
    const tid=e.dataTransfer.getData("tid");if(!tid)return;
    const t=(tasks||[]).find(x=>x.id===tid);if(!t)return;
    if(groupBy==="status") changeStatus(t,colId);
    else if(groupBy==="priority"){syncUp({...t,priority:colId})}
    else if(groupBy==="assignee"){syncUp({...t,assignees:colId==="__unassigned"?[]:[colId]})}
  };

  /* ── board columns ── */
  const boardCols=useMemo(()=>{
    if(groupBy==="status") return TASK_STATUS.map(s=>({id:s.id,name:s.n,c:s.c,tasks:boardItems.filter(t=>(t.status||"todo")===s.id)}));
    if(groupBy==="priority") return PRI.map(p=>({id:p,name:p,c:priC[p],tasks:allTasks.filter(t=>(t.priority||"Medium")===p)}));
    if(groupBy==="assignee"){
      const names=[...new Set((tasks||[]).flatMap(t=>t.assignees||[]).filter(Boolean))].sort();
      const cols=names.map(n=>({id:n,name:n,c:"#3B82F6",tasks:allTasks.filter(t=>(t.assignees||[]).includes(n))}));
      const una=allTasks.filter(t=>!(t.assignees||[]).length);
      if(una.length>0) cols.unshift({id:"__unassigned",name:"Unassigned",c:"#64748B",tasks:una});
      return cols;
    }
    return[];
  },[groupBy,allTasks,boardItems,tasks]);

  /* ── create task handler ── */
  const handleCreate=()=>{
    if(!newTask.title.trim()){alert("Enter a task title");return}
    const t={title:newTask.title,due:newTask.due,priority:newTask.priority,type:newTask.type,note:newTask.note,assignees:newTask.assignees,status:newTask.status||"todo",subtasks:[],reminder:""};
    onAddTask(t);
    setNewTask({title:"",due:"",priority:"Medium",type:"general",note:"",assignees:[],status:"todo"});
    setShowNew(false);
  };

  /* ── Task Card (board) ── */
  const TaskCard=({t})=>{
    const dl=dueLabel(t);const st=stMap[t.status||"todo"]||stMap.todo;const sp=subProg(t);const isActive=vwId===t.id;
    return(
      <div draggable onDragStart={e=>onDragStart(e,t)} onDragEnd={onDragEnd} onClick={()=>setVwId(t.id)}
        className="kcard" style={{background:isActive?(T.ac+"10"):T.sf,borderRadius:12,border:"1px solid "+(isActive?T.ac+"40":T.bd+"40"),padding:10,cursor:"grab",transition:"box-shadow .15s,border-color .15s"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
          <button onClick={e=>{e.stopPropagation();toggleDone(t)}} style={{background:"transparent",border:"none",cursor:"pointer",padding:0,display:"flex",color:t.status==="done"?"#10B981":stColor(t.status||"todo"),flexShrink:0}}>
            {t.status==="done"?<Ic n="chk" s={14}/>:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}
          </button>
          <span style={{fontSize:12,fontWeight:600,flex:1,textDecoration:t.status==="done"?"line-through":"none",color:t.status==="done"?T.tm:T.tx,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.title}</span>
          <DotsMenu items={[{ic:"eye",l:"Open",act:()=>setVwId(t.id)},{ic:"del",l:"Delete",danger:true,act:()=>deleteTask(t)}]}/>
        </div>
        {/* subtask progress */}
        {sp&&<div style={{marginBottom:5}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
            <div style={{flex:1,height:3,borderRadius:2,background:T.bd+"40",overflow:"hidden"}}><div style={{width:sp.pct+"%",height:"100%",borderRadius:2,background:sp.pct===100?"#10B981":"#3B82F6",transition:"width .2s"}}/></div>
            <span style={{fontSize:9,fontWeight:600,color:T.tm}}>{sp.done}/{sp.total}</span>
          </div>
        </div>}
        <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
          {groupBy!=="status"&&<span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:4,background:st.c+"18",color:st.c}}>{st.n}</span>}
          <Bdg type={t.priority||"Medium"} noDot/>
          <span style={{fontSize:9,fontWeight:600,padding:"1px 6px",borderRadius:4,background:(typeC[t.type]||"#64748B")+"15",color:typeC[t.type]||"#64748B",display:"flex",alignItems:"center",gap:3}}><Ic n={typeIc[t.type]||"tasks"} s={8}/>{t.type||"general"}</span>
          {t.reminder&&<span style={{fontSize:9,color:"#F59E0B",display:"flex",alignItems:"center",gap:2}}><Ic n="bell" s={8}/></span>}
          {dl&&<span style={{fontSize:9,fontWeight:600,color:dl.color,marginLeft:"auto"}}>{dl.text}</span>}
        </div>
        {(t.assignees||[]).length>0&&<div style={{display:"flex",marginTop:5}}>{(t.assignees||[]).slice(0,3).map((nm,i)=>{
          const u=(users||[]).find(x=>x.name===nm);
          return<span key={nm} title={nm} style={{width:18,height:18,borderRadius:5,background:T.gr,display:"flex",alignItems:"center",justifyContent:"center",color:"#FFF",fontSize:7,fontWeight:700,marginLeft:i>0?-5:0,border:"1.5px solid "+T.sf,zIndex:3-i}}>{u?.av||nm.slice(0,2).toUpperCase()}</span>;
        })}</div>}
      </div>
    );
  };

  /* ═══════ RENDER ═══════ */
  return (
    <div style={{display:"flex",gap:0,position:"relative"}}>
      {/* ── Main Content (shrinks when panel open) ── */}
      <div style={{flex:1,minWidth:0,transition:"margin-right .25s cubic-bezier(.4,0,.2,1)",marginRight:vwTask?580:0}}>

      {/* ── Stat Cards ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}} className="dash-stats">
        {[{l:"Active",v:counts.active,c:"#3B82F6",f:"active"},{l:"Due Today",v:counts.today,c:"#F59E0B",f:"today"},{l:"Overdue",v:counts.overdue,c:"#EF4444",f:"overdue"},{l:"Completed",v:counts.done,c:"#10B981",f:"done"}].map(s=>(
          <div key={s.l} className="cr stat-card" onClick={()=>setFilter(s.f)} style={{padding:"12px 16px",borderRadius:14,border:"1px solid "+(filter===s.f?s.c+"40":T.bd),background:filter===s.f?s.c+"08":T.sf,cursor:"pointer"}}>
            <div style={{fontSize:20,fontWeight:800,color:s.c}}>{s.v}</div>
            <div style={{fontSize:10,color:T.tm,fontWeight:500}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:3,background:T.sl,borderRadius:10,padding:2,border:"1px solid "+T.bd+"60"}}>
          {[["active","Active"],["today","Today"],["overdue","Overdue"],["done","Done"],["all","All"]].map(([k,l])=><button key={k} onClick={()=>setFilter(k)} style={{border:"none",borderRadius:8,cursor:"pointer",fontFamily:BF,padding:"6px 12px",fontSize:11,fontWeight:600,background:filter===k?T.ac:"transparent",color:filter===k?"#FFF":T.tm}}>{l}</button>)}
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
          <button onClick={()=>setMyTasks(p=>!p)} style={{border:"none",borderRadius:8,cursor:"pointer",fontFamily:BF,padding:"6px 12px",fontSize:11,fontWeight:600,background:myTasks?T.ac+"18":"transparent",color:myTasks?T.ac:T.tm,display:"flex",alignItems:"center",gap:4}}><Ic n="usr" s={11}/> My Tasks</button>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tasks..." style={{padding:"7px 12px",borderRadius:8,border:"1px solid "+T.bd,background:isDark()?"rgba(255,255,255,.04)":"rgba(0,0,0,.02)",color:T.tx,fontSize:12,fontFamily:BF,minWidth:120,maxWidth:200,outline:"none"}}/>
          <select value={groupBy} onChange={e=>setGroupBy(e.target.value)} style={{padding:"6px 10px",borderRadius:8,border:"1px solid "+T.bd,background:isDark()?"rgba(255,255,255,.04)":"rgba(0,0,0,.02)",color:T.tx,fontSize:11,fontFamily:BF,cursor:"pointer",outline:"none"}}>
            <option value="status">By Status</option>
            <option value="assignee">By Assignee</option>
            <option value="priority">By Priority</option>
          </select>
          <div style={{display:"flex",gap:2,background:T.sl,borderRadius:8,padding:2,border:"1px solid "+T.bd}}>
            <button onClick={()=>setViewMode("board")} style={{border:"none",borderRadius:6,cursor:"pointer",fontFamily:BF,display:"flex",alignItems:"center",gap:4,padding:"6px 12px",fontSize:11,fontWeight:600,background:viewMode==="board"?T.ac:"transparent",color:viewMode==="board"?"#FFF":T.tm}}><Ic n="cols" s={11}/> Board</button>
            <button onClick={()=>setViewMode("list")} style={{border:"none",borderRadius:6,cursor:"pointer",fontFamily:BF,display:"flex",alignItems:"center",gap:4,padding:"6px 12px",fontSize:11,fontWeight:600,background:viewMode==="list"?T.ac:"transparent",color:viewMode==="list"?"#FFF":T.tm}}><Ic n="rows" s={11}/> List</button>
          </div>
          {!vwTask&&<button onClick={()=>setShowNew(true)} style={{...sBG(),padding:"7px 14px",fontSize:12}}><Ic n="plus" s={12}/> {t("task.newTask")}</button>}
        </div>
      </div>

      {/* ── Inline New Task Form ── */}
      {showNew&&<div style={{padding:16,borderRadius:14,background:T.sf,border:"1px solid "+T.ac+"30",marginBottom:16,animation:"su .2s ease"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}><div style={{width:4,height:18,borderRadius:2,background:T.ac}}/><span style={{fontWeight:700,fontSize:13}}>New Task</span></div>
        <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          <div style={{flex:2,minWidth:180}}><input value={newTask.title} onChange={e=>setNewTask(p=>({...p,title:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter")handleCreate()}} placeholder="What needs to be done?" style={{...sIG(),padding:"8px 12px",fontSize:12}} autoFocus/></div>
          <div style={{flex:1,minWidth:120}}><input type="date" value={newTask.due} onChange={e=>setNewTask(p=>({...p,due:e.target.value}))} style={{...sIG(),padding:"8px 12px",fontSize:12}}/></div>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          <select value={newTask.priority} onChange={e=>setNewTask(p=>({...p,priority:e.target.value}))} style={{...sSG(),padding:"8px 12px",fontSize:12,flex:1}}>{PRI.map(p=><option key={p} value={p}>{p}</option>)}</select>
          <select value={newTask.status} onChange={e=>setNewTask(p=>({...p,status:e.target.value}))} style={{...sSG(),padding:"8px 12px",fontSize:12,flex:1}}>{TASK_STATUS.map(s=><option key={s.id} value={s.id}>{s.n}</option>)}</select>
          <select value={newTask.type} onChange={e=>setNewTask(p=>({...p,type:e.target.value}))} style={{...sSG(),padding:"8px 12px",fontSize:12,flex:1}}><option value="general">General</option><option value="order">Order</option><option value="shipment">Shipment</option><option value="customer">Customer</option><option value="vendor">Vendor</option></select>
          <select value={newTask.assignees?.[0]||""} onChange={e=>setNewTask(p=>({...p,assignees:e.target.value?[e.target.value]:[]}))} style={{...sSG(),padding:"8px 12px",fontSize:12,flex:1}}><option value="">Assign to...</option>{(users||[]).filter(u=>u.on!==false&&u.name).map(u=><option key={u.name} value={u.name}>{u.name}</option>)}</select>
        </div>
        <textarea value={newTask.note} onChange={e=>setNewTask(p=>({...p,note:e.target.value}))} placeholder="Notes (optional)..." style={{...sIG(),padding:"8px 12px",fontSize:12,marginBottom:10,minHeight:48,resize:"vertical"}} rows={2}/>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>handleCreate()} style={{...sBG(),padding:"8px 16px",fontSize:12}}><Ic n="chk" s={12}/> Create Task</button>
          <button onClick={()=>{setShowNew(false);setNewTask({title:"",due:"",priority:"Medium",type:"general",note:"",assignees:[],status:"todo"})}} style={{...sB2G(),padding:"8px 16px",fontSize:12}}><Ic n="x" s={11}/> Cancel</button>
        </div>
      </div>}

      {/* ── Content ── */}
      {allTasks.length===0&&!showNew ? <EmptyPg ic="tasks" title={filter==="done"?"No completed tasks":filter==="overdue"?"No overdue tasks":filter==="today"?"Nothing due today":"No tasks"} sub="Create reminders for follow-ups and deadlines." act={()=>setShowNew(true)} actLabel="Create Task"/> :

      viewMode==="board" ? (
        /* ═══ BOARD VIEW ═══ */
        <div className="task-board" style={{display:"grid",gridTemplateColumns:"repeat("+boardCols.length+",minmax(180px,1fr))",gap:12,overflowX:"auto",paddingBottom:8}}>
          {boardCols.map(col=>(
            <div key={col.id}
              onDragOver={e=>onColDragOver(e,col)} onDragLeave={onColDragLeave}
              onDrop={e=>onColDrop(e,col.id)}
              style={{background:T.sf+"90",borderRadius:16,padding:12,border:"1px solid "+(isDark()?"rgba(255,255,255,.05)":T.bd+"50"),minHeight:200,transition:"background .15s, border-color .15s"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:col.c}}/>
                <span style={{fontWeight:700,fontSize:12}}>{col.name}</span>
                <span style={{fontSize:10,fontWeight:700,color:T.tm,background:T.sl,padding:"1px 6px",borderRadius:8}}>{col.tasks.length}</span>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {col.tasks.map(t=><TaskCard key={t.id} t={t}/>)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ═══ LIST VIEW ═══ */
        <div style={{background:T.sf+"80",borderRadius:16,border:"1px solid "+(isDark()?"rgba(255,255,255,.05)":T.bd+"50"),overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 80px 80px 60px 80px",gap:6,padding:"8px 16px",borderBottom:"1px solid "+T.bd+"40",fontSize:9,fontWeight:700,color:T.tm,textTransform:"uppercase",letterSpacing:".05em"}} className="task-list-header">
            <span/><span>Task</span><span>Status</span><span className="tl-hide">Priority</span><span className="tl-hide">Due</span><span className="tl-hide">Subs</span><span>Assignee</span>
          </div>
          {allTasks.map((t,i)=>{
            const dl=dueLabel(t);const st=stMap[t.status||"todo"]||stMap.todo;const sp=subProg(t);const isActive=vwId===t.id;
            return(
              <div key={t.id} style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 80px 80px 60px 80px",gap:6,padding:"10px 16px",borderBottom:i<allTasks.length-1?"1px solid "+T.bd+"40":"none",alignItems:"center",transition:"background .12s",background:isActive?T.ac+"08":"transparent",cursor:"pointer"}} className="cr task-list-row" onClick={()=>setVwId(t.id)}>
                <button onClick={e=>{e.stopPropagation();toggleDone(t)}} style={{background:"transparent",border:"none",cursor:"pointer",padding:0,display:"flex",color:t.status==="done"?"#10B981":stColor(t.status||"todo"),flexShrink:0}}>
                  {t.status==="done"?<Ic n="chk" s={16}/>:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}
                </button>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,textDecoration:t.status==="done"?"line-through":"none",color:t.status==="done"?T.tm:T.tx,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.title}</div>
                  <div style={{display:"flex",gap:4,alignItems:"center",marginTop:2}}>
                    <span style={{fontSize:9,fontWeight:600,color:typeC[t.type]||"#64748B",display:"flex",alignItems:"center",gap:2}}><Ic n={typeIc[t.type]||"tasks"} s={8}/>{t.type||"general"}</span>
                    {t.refId&&<span style={{fontSize:9,color:"#0891B2",fontWeight:600}}>{t.refId}</span>}
                    {t.reminder&&<span style={{fontSize:9,color:"#F59E0B",display:"flex",alignItems:"center",gap:2}}><Ic n="bell" s={8}/></span>}
                  </div>
                </div>
                <select value={t.status||"todo"} onClick={e=>e.stopPropagation()} onChange={e=>{e.stopPropagation();changeStatus(t,e.target.value)}} style={{fontSize:10,fontWeight:700,padding:"2px 4px",borderRadius:6,border:"1px solid "+st.c+"30",background:st.c+"12",color:st.c,cursor:"pointer",outline:"none",fontFamily:BF}}>
                  {TASK_STATUS.map(s=><option key={s.id} value={s.id}>{s.n}</option>)}
                </select>
                <Bdg type={t.priority||"Medium"} noDot/>
                <span className="tl-hide">{dl?<span style={{fontSize:10,fontWeight:600,color:dl.color}}>{dl.text}</span>:<span style={{fontSize:10,color:"#94A3B8"}}>—</span>}</span>
                <span className="tl-hide">{sp?<span style={{fontSize:10,fontWeight:600,color:sp.pct===100?"#10B981":T.tm}}>{sp.done}/{sp.total}</span>:<span style={{fontSize:10,color:"#94A3B8"}}>—</span>}</span>
                <div style={{display:"flex",alignItems:"center"}}>
                  {(t.assignees||[]).length>0?(t.assignees||[]).slice(0,2).map((nm,j)=>{const u=(users||[]).find(x=>x.name===nm);return<span key={nm} title={nm} style={{width:20,height:20,borderRadius:6,background:T.gr,display:"flex",alignItems:"center",justifyContent:"center",color:"#FFF",fontSize:7,fontWeight:700,marginLeft:j>0?-4:0,border:"1.5px solid "+T.sf}}>{u?.av||nm.slice(0,2).toUpperCase()}</span>}):<span style={{fontSize:10,color:"#94A3B8"}}>—</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* ═══ RIGHT PANEL ═══ */}
      {/* ═══════════════════════════════════════════ */}
      {vwTask&&<TaskPanel t={vwTask} close={()=>setVwId(null)} syncUp={syncUp} changeStatus={changeStatus} toggleDone={toggleDone} deleteTask={deleteTask} dueLabel={dueLabel} goRef={goRef} users={users} orders={orders} ships={ships} custs={custs} vends={vends}/>}
    </div>
  );
}


/* ═══════════════════════════════════════════════════ */
/* ═══ TASK DETAIL PANEL ═══                          */
/* ═══════════════════════════════════════════════════ */
function TaskPanel({t,close,syncUp,changeStatus,toggleDone,deleteTask,dueLabel,goRef,users,orders,ships,custs,vends}) {
  const [editTitle,setEditTitle]=useState(false);
  const [titleDraft,setTitleDraft]=useState(t.title);
  const [subInput,setSubInput]=useState("");
  const [showRemPick,setShowRemPick]=useState(false);
  const [customRem,setCustomRem]=useState("");
  const titleRef=useRef(null);

  // Sync title draft when task changes
  useEffect(()=>{setTitleDraft(t.title);setEditTitle(false)},[t.id]);

  const dl=dueLabel(t);const sp=subProg(t);
  const stObj=stMap[t.status||"todo"]||stMap.todo;

  /* ── field updater ── */
  const upd=(fields)=>syncUp({...t,...fields});

  /* ── subtask ops ── */
  const addSub=()=>{
    if(!subInput.trim()){alert("Enter subtask text");return}
    const ns={id:"sub-"+Date.now(),title:subInput.trim(),done:false};
    upd({subtasks:[...(t.subtasks||[]),ns]});
    setSubInput("");
  };
  const togSub=(sid)=>{upd({subtasks:(t.subtasks||[]).map(s=>s.id===sid?{...s,done:!s.done}:s)})};
  const delSub=(sid)=>{upd({subtasks:(t.subtasks||[]).filter(s=>s.id!==sid)})};

  /* ── reminder helpers ── */
  const setReminder=(iso)=>{upd({reminder:iso});setShowRemPick(false)};
  const clearReminder=()=>{upd({reminder:""});setShowRemPick(false)};
  const remQuick=(label)=>{
    const now=new Date();
    if(label==="1h"){now.setHours(now.getHours()+1)}
    else if(label==="3h"){now.setHours(now.getHours()+3)}
    else if(label==="tom"){now.setDate(now.getDate()+1);now.setHours(9,0,0,0)}
    else if(label==="mon"){const d=now.getDay();const diff=d===0?1:(8-d);now.setDate(now.getDate()+diff);now.setHours(9,0,0,0)}
    setReminder(now.toISOString());
  };

  const fmtRem=(iso)=>{
    if(!iso)return"";
    const d=new Date(iso);const diff=d-new Date();const hrs=Math.round(diff/36e5);
    if(hrs<0)return"Overdue · "+d.toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"});
    if(hrs<24)return"In "+hrs+"h · "+d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
    return d.toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"});
  };

  /* ── save title ── */
  const commitTitle=()=>{if(titleDraft.trim()&&titleDraft!==t.title)upd({title:titleDraft.trim()});setEditTitle(false)};

  /* linked entity info */
  const refEntity=t.refId?(t.type==="order"?(orders||[]).find(o=>o.id===t.refId):t.type==="customer"?(custs||[]).find(c=>c.id===t.refId):t.type==="vendor"?(vends||[]).find(v=>v.id===t.refId):t.type==="shipment"?(ships||[]).find(s=>s.id===t.refId):null):null;
  const refName=refEntity?(refEntity.nm||refEntity.co||refEntity.id||t.refId):t.refId;

  return(
    createPortal(<>
      {/* backdrop */}
      <div className="view-panel-overlay" onClick={()=>close()} style={{position:"fixed",top:"var(--stq-pt,66px)",left:"var(--stq-pl,252px)",right:10,bottom:14,zIndex:998,background:"rgba(0,0,0,.25)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",animation:"fi .15s ease",borderRadius:20}}/>
      {/* panel */}
      <div className="view-panel" style={{position:"fixed",top:"var(--stq-pt,66px)",right:10,bottom:14,width:540,maxWidth:"calc(100vw - 20px)",zIndex:999,background:isDark()?"#0a0a0a":"#FFFFFF",border:"1px solid "+(isDark()?"rgba(255,255,255,.08)":T.bd),display:"flex",flexDirection:"column",animation:"si .25s ease",boxShadow:isDark()?"-4px 0 32px rgba(0,0,0,.3)":"0 4px 32px rgba(0,0,0,.08),0 0 0 .5px rgba(0,0,0,.03)",borderRadius:20,color:T.tx,overflow:"hidden"}}>

        {/* ── Header ── */}
        <div style={{padding:"20px 24px 16px",borderBottom:"1px solid "+T.bd+"40",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            {/* status color strip */}
            <div style={{width:4,height:40,borderRadius:3,background:stObj.c,flexShrink:0,marginTop:2}}/>
            <div style={{flex:1,minWidth:0}}>
              {editTitle?
                <input ref={titleRef} autoFocus value={titleDraft} onChange={e=>setTitleDraft(e.target.value)} onBlur={()=>commitTitle()} onKeyDown={e=>{if(e.key==="Enter")commitTitle();if(e.key==="Escape"){setTitleDraft(t.title);setEditTitle(false)}}} style={{...sIG(),fontSize:18,fontWeight:800,fontFamily:HF,padding:"4px 8px",letterSpacing:"-.02em"}}/>
              : <h2 onClick={()=>setEditTitle(true)} style={{fontSize:18,fontWeight:800,fontFamily:HF,margin:0,cursor:"text",letterSpacing:"-.02em",lineHeight:1.3,color:T.tx}}>{t.title}</h2>}
              <div style={{display:"flex",gap:6,alignItems:"center",marginTop:6,flexWrap:"wrap"}}>
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6,background:stObj.c+"18",color:stObj.c}}>{stObj.n}</span>
                <Bdg type={t.priority||"Medium"} noDot/>
                <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:6,background:(typeC[t.type]||"#64748B")+"15",color:typeC[t.type]||"#64748B",display:"flex",alignItems:"center",gap:3}}><Ic n={typeIc[t.type]||"tasks"} s={9}/>{t.type||"general"}</span>
              </div>
            </div>
            <button onClick={()=>close()} style={{background:"transparent",border:"none",cursor:"pointer",color:T.tm,padding:4,display:"flex"}}><Ic n="x" s={18}/></button>
          </div>
          {/* action bar — status pipeline */}
          <div style={{display:"flex",gap:4,marginTop:14,alignItems:"center",flexWrap:"wrap"}}>
            {TASK_STATUS.map((s,i)=>{const isCur=(t.status||"todo")===s.id;return(
              <React.Fragment key={s.id}>
                {i>0&&<div style={{width:12,height:1,background:T.bd+"60"}}/>}
                <button onClick={()=>changeStatus(t,s.id)} style={{padding:"6px 14px",borderRadius:8,border:"1px solid "+(isCur?s.c+"50":T.bd+"40"),background:isCur?s.c+"12":"transparent",color:isCur?s.c:T.tm,fontSize:10,fontWeight:isCur?700:500,cursor:"pointer",fontFamily:BF,transition:"all .15s",display:"flex",alignItems:"center",gap:4}}>
                  {isCur&&<div style={{width:6,height:6,borderRadius:"50%",background:s.c}}/>}{s.n}
                </button>
              </React.Fragment>
            )})}
          </div>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <button onClick={()=>setShowRemPick(p=>!p)} style={{padding:"7px 14px",borderRadius:9,border:"1px solid "+(t.reminder?"#F59E0B":"#64748B")+"40",background:t.reminder?"#F59E0B08":"transparent",color:t.reminder?"#F59E0B":T.tm,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:BF,display:"flex",alignItems:"center",gap:5}}><Ic n="bell" s={12}/>{t.reminder?"Reminder Set":"Set Reminder"}</button>
            <div style={{marginLeft:"auto"}}><DotsMenu items={[{ic:"del",l:"Delete Task",danger:true,act:()=>deleteTask(t)}]}/></div>
          </div>
        </div>

        {/* ── Body (scrollable) ── */}
        <div style={{flex:1,overflowY:"auto",padding:"20px 24px 24px"}}>

          {/* ── Reminder Section ── */}
          {(showRemPick||t.reminder)&&<div style={{marginBottom:18,padding:"12px 16px",borderRadius:14,background:"rgba(245,158,11,.05)",border:"1px solid rgba(245,158,11,.15)"}}>
            {t.reminder&&!showRemPick?
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Ic n="bell" s={14}/>
                <span style={{fontSize:12,fontWeight:600,color:"#F59E0B",flex:1}}>{fmtRem(t.reminder)}</span>
                <button onClick={()=>setShowRemPick(true)} style={{fontSize:10,fontWeight:600,color:"#F59E0B",background:"rgba(245,158,11,.1)",border:"none",cursor:"pointer",padding:"4px 10px",borderRadius:6,fontFamily:BF}}>Edit</button>
                <button onClick={()=>clearReminder()} style={{fontSize:10,fontWeight:600,color:T.tm,background:"transparent",border:"1px solid "+T.bd,cursor:"pointer",padding:"4px 8px",borderRadius:6,fontFamily:BF}}>Clear</button>
              </div>
            : <div>
                <div style={{fontSize:11,fontWeight:700,color:"#F59E0B",marginBottom:8}}>Set Reminder</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {[["1h","In 1 hour"],["3h","In 3 hours"],["tom","Tomorrow 9am"],["mon","Next Monday"]].map(([k,l])=>
                    <button key={k} onClick={()=>remQuick(k)} style={{padding:"5px 12px",borderRadius:8,border:"1px solid rgba(245,158,11,.25)",background:"rgba(245,158,11,.06)",color:"#D97706",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:BF}}>{l}</button>
                  )}
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <input type="datetime-local" value={customRem} onChange={e=>setCustomRem(e.target.value)} style={{...sIG(),padding:"6px 10px",fontSize:11,flex:1}}/>
                  <button onClick={()=>{if(customRem)setReminder(new Date(customRem).toISOString())}} disabled={!customRem} style={{...sBG(),padding:"6px 14px",fontSize:11,opacity:customRem?1:.4}}>Set</button>
                  <button onClick={()=>{setShowRemPick(false);setCustomRem("")}} style={{background:"transparent",border:"none",color:T.tm,cursor:"pointer"}}><Ic n="x" s={14}/></button>
                </div>
              </div>
            }
          </div>}

          {/* ── Detail Grid ── */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18}}>
            <div style={{padding:12,borderRadius:12,background:T.sl,border:"1px solid "+T.bd}}>
              <div style={{fontSize:9,color:T.tm,fontWeight:600,textTransform:"uppercase",marginBottom:4}}>Priority</div>
              <select value={t.priority||"Medium"} onChange={e=>upd({priority:e.target.value})} style={{fontSize:12,fontWeight:700,color:priC[t.priority||"Medium"],background:"transparent",border:"none",outline:"none",cursor:"pointer",fontFamily:BF,width:"100%"}}>
                {PRI.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{padding:12,borderRadius:12,background:T.sl,border:"1px solid "+T.bd}}>
              <div style={{fontSize:9,color:T.tm,fontWeight:600,textTransform:"uppercase",marginBottom:4}}>Type</div>
              <select value={t.type||"general"} onChange={e=>upd({type:e.target.value})} style={{fontSize:12,fontWeight:600,color:typeC[t.type]||"#64748B",background:"transparent",border:"none",outline:"none",cursor:"pointer",fontFamily:BF,width:"100%"}}>
                <option value="general">General</option><option value="order">Order</option><option value="shipment">Shipment</option><option value="customer">Customer</option><option value="vendor">Vendor</option>
              </select>
            </div>
            <div style={{padding:12,borderRadius:12,background:T.sl,border:"1px solid "+T.bd}}>
              <div style={{fontSize:9,color:T.tm,fontWeight:600,textTransform:"uppercase",marginBottom:4}}>Due Date</div>
              <input type="date" value={t.due||""} onChange={e=>upd({due:e.target.value})} style={{fontSize:12,fontWeight:600,color:dl?.color||T.ts,background:"transparent",border:"none",outline:"none",cursor:"pointer",fontFamily:BF,width:"100%"}}/>
            </div>
            <div style={{padding:12,borderRadius:12,background:T.sl,border:"1px solid "+T.bd}}>
              <div style={{fontSize:9,color:T.tm,fontWeight:600,textTransform:"uppercase",marginBottom:4}}>Assigned To</div>
              <select value={(t.assignees||[])[0]||""} onChange={e=>upd({assignees:e.target.value?[e.target.value]:[]})} style={{fontSize:12,fontWeight:600,color:T.ts,background:"transparent",border:"none",outline:"none",cursor:"pointer",fontFamily:BF,width:"100%"}}>
                <option value="">Unassigned</option>
                {(users||[]).filter(u=>u.on!==false&&u.name).map(u=><option key={u.name} value={u.name}>{u.name}</option>)}
              </select>
            </div>
          </div>

          {/* ── Subtasks ── */}
          <div style={{marginBottom:18}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <span style={{fontSize:13,fontWeight:700}}>Subtasks</span>
              {sp&&<span style={{fontSize:10,fontWeight:600,color:sp.pct===100?"#10B981":T.tm}}>{sp.done} of {sp.total}</span>}
            </div>
            {sp&&<div style={{height:4,borderRadius:3,background:T.bd+"40",overflow:"hidden",marginBottom:10}}>
              <div style={{width:sp.pct+"%",height:"100%",borderRadius:3,background:sp.pct===100?"#10B981":"#3B82F6",transition:"width .25s ease"}}/>
            </div>}
            <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}>
              {(t.subtasks||[]).map(s=>(
                <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:10,background:s.done?(isDark()?"rgba(16,185,129,.04)":"rgba(16,185,129,.03)"):(isDark()?"rgba(255,255,255,.02)":"rgba(0,0,0,.015)"),border:"1px solid "+(s.done?"rgba(16,185,129,.15)":T.bd+"30"),transition:"all .12s"}}>
                  <button onClick={()=>togSub(s.id)} style={{background:"transparent",border:"none",cursor:"pointer",padding:0,display:"flex",color:s.done?"#10B981":"#94A3B8",flexShrink:0}}>
                    {s.done?<Ic n="chk" s={14}/>:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}
                  </button>
                  <span style={{flex:1,fontSize:12,fontWeight:500,textDecoration:s.done?"line-through":"none",color:s.done?T.tm:T.tx}}>{s.title}</span>
                  <button onClick={()=>delSub(s.id)} style={{background:"transparent",border:"none",cursor:"pointer",padding:0,display:"flex",color:"#94A3B8",opacity:.5}} title="Remove"><Ic n="x" s={12}/></button>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:6}}>
              <input value={subInput} onChange={e=>setSubInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addSub()}} placeholder="Add a subtask..." style={{...sIG(),padding:"7px 12px",fontSize:12,flex:1}}/>
              <button onClick={()=>addSub()} disabled={!subInput.trim()} style={{...sB2G(),padding:"7px 14px",fontSize:11,opacity:subInput.trim()?1:.35}}>Add</button>
            </div>
          </div>

          {/* ── Notes ── */}
          <div style={{marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Notes</div>
            <textarea value={t.note||""} onChange={e=>upd({note:e.target.value})} placeholder="Add notes..." style={{...sIG(),padding:"10px 14px",fontSize:12,minHeight:80,resize:"vertical"}} rows={3}/>
          </div>

          {/* ── Linked Entity ── */}
          {t.refId&&<div style={{marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Linked {t.type}</div>
            <div onClick={()=>goRef(t)} style={{padding:"12px 16px",borderRadius:12,background:(typeC[t.type]||T.ac)+"06",border:"1px solid "+(typeC[t.type]||T.ac)+"15",cursor:"pointer",display:"flex",alignItems:"center",gap:10,transition:"background .12s"}} className="cr">
              <div style={{width:32,height:32,borderRadius:8,background:(typeC[t.type]||"#64748B")+"15",display:"flex",alignItems:"center",justifyContent:"center",color:typeC[t.type]||"#64748B"}}><Ic n={typeIc[t.type]||"tasks"} s={14}/></div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:700,color:typeC[t.type]||T.ac}}>{t.refId}</div>
                {refName&&refName!==t.refId&&<div style={{fontSize:10,color:T.tm}}>{refName}</div>}
              </div>
              <span style={{fontSize:10,fontWeight:600,color:typeC[t.type]||T.ac}}>Open →</span>
            </div>
          </div>}

          {/* ── Meta ── */}
          <div style={{fontSize:10,color:T.tm,display:"flex",flexDirection:"column",gap:3,paddingTop:8,borderTop:"1px solid "+T.bd+"30"}}>
            {t.createdBy&&<span>Created by {t.createdBy}{t.createdAt?" · "+relTime(t.createdAt):""}</span>}
            {t.doneAt&&<span style={{color:"#10B981"}}>Completed {relTime(t.doneAt)}</span>}
          </div>
        </div>
      </div>
    </>, document.body)
  );
}

export default TasksPg;
